import { Pagesection } from '@ktag/styleguide/dist/react';
import ApiContext from 'api/ApiContext';
import { useTranslation } from 'react-i18next';
import React, { useContext } from 'react';

export interface FormattedCandidate {
  id: number;
  candidateNumber: number;
  firstName: string;
  lastName: string;
  inOfficeSince: number;
  isUpToNow: boolean;
  party: string;
  isArchiveCandidate: boolean;
  location: string;
  votes: number;
  list: string;
  listName: string;
  gender: string;
  elected: boolean;
  photoPath?: string;
  countingCircle?: string;
  bfsNumber?: string;
  partyColor?: string;
}

interface Props {
  candidates: FormattedCandidate[];
}
const CandidatePortraits = ({ candidates }: Props) => {
  const { t } = useTranslation('wap');
  const { apiUrl, archiveApiUrl } = useContext(ApiContext);

  const getCandidatePhotoPath = (candidate: FormattedCandidate) => {
    const baseUrl = candidate.isArchiveCandidate
      ? archiveApiUrl.replace('/data', '')
      : apiUrl;
    return `${baseUrl}${candidate.photoPath}`;
  };

  const handleCandidateImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    e.currentTarget.src = `${process.env.PUBLIC_URL}/images/missingportrait_placeholder.svg`;
  };

  const getAltText = (candidate: FormattedCandidate) => {
    if (candidate.photoPath) {
      return `${t('portrait')} ${candidate.firstName} ${candidate.lastName}`;
    }

    return `${t('portrait_no_image')}`;
  };

  const getName = (candidate: FormattedCandidate) => {
    if (candidate.firstName || candidate.lastName) {
      return `${candidate.firstName} ${candidate.lastName}`;
    }
    return t('open_seat');
  };

  const getIsUpToNow = (isUpToNow?: boolean, inOfficeSince?: number) => {
    // this is the case for the "offen" candidates / placeholders. There is no info apart from the list name
    if (isUpToNow === null) {
      return (
        <>
          <br />
          <br />
          <br />
        </>
      );
    }

    if (isUpToNow) {
      if (inOfficeSince) {
        return (
          <span className="candidateportrait__details-up-to-now">
            <span>{t('is_up_to_now')}</span>
            <span>{` (${t('since', {
              year: inOfficeSince,
            })})`}</span>
            <br />
          </span>
        );
      }

      return (
        <span className="candidateportrait__details-up-to-now">
          <span>{t('is_up_to_now')}</span>
          <br />
        </span>
      );
    }

    return (
      <>
        <span>{t('new')}</span>
        <br />
      </>
    );
  };

  return (
    <Pagesection type="slender">
      <ul className="candidateportraits">
        {candidates?.map((candidate) => (
          // candidate.id is only unique within the same list. Therefore, we add the list to the key
          <li className="candidateportrait" key={`${candidate.id}-${candidate.list}`}>
            <img
              src={getCandidatePhotoPath(candidate)}
              alt={getAltText(candidate)}
              className="candidateportrait__photo"
              onError={handleCandidateImageError}
            />
            <div className="candidateportrait__info">
              <div className="candidateportrait__name">{getName(candidate)}</div>
              <div className="candidateportrait__details">
                {candidate.listName && (
                  <>
                    {candidate.listName}
                    <br />
                  </>
                )}
                {getIsUpToNow(candidate.isUpToNow, candidate.inOfficeSince)}
                {candidate.location && (
                  <>
                    {candidate.location}
                    <br />
                  </>
                )}
                {candidate.votes && t('absolute_votes', { count: candidate.votes })}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Pagesection>
  );
};

export default CandidatePortraits;
